import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useFormErrorsActions } from '../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

import { SelectContractForm } from '../../../../styledComponents/Forms/SelectContractForm/SelectContractForm';
import {
  LeftContainer,
  RightContainer,
  PageRootColumns,
} from '../../../../styledComponents/Page/Page';
import { useAAEditSubscriptionRenewForm } from './aAEditSubscriptionRenewPageForm.hooks';
import SubscriptionSummaryForm from '../../../../styledComponents/FormSummary/SubscriptionSummary/SubscriptionSummary';
import Spinner from '../../../../styledComponents/Spinner/Spinner';
import FormErrorActivator from '../../../../styledComponents/FormErrorProvider/FormErrorActivator/FormErrorActivator';
import WarningsConfirmationProvider from '../../../../styledComponents/WarningsConfirmation/WarningsConfirmationProvider';
import WarningsConfirmationModal from '../../../../styledComponents/WarningsConfirmation/WarningsConfirmationModal/WarningsConfirmationModal';
import { ProductType } from '../../../../models/ProductType';
import FormSubmitButtons from '../../../../styledComponents/UI/FormSubmitButtons/FormSubmitButtons';
import AirAmbulanceRenewSubscriptionForm from '../../../../styledComponents/RenewSubscriptionFormPage/FormSections/AirAmbulanceRenewSubscription/AirAmbulanceRenewSubscriptionForm';
import { validateAAEditRenewSubscriptionForm } from './aAEditSubscriptionRenewPageForm.validation';
import { AARenewSubscriptionSummaryLoader } from '../../../../styledComponents/RenewSubscriptionFormPage/FormSections/AirAmbulanceRenewSubscription/AARenewSubscriptionSummaryLoader/AARenewSubscriptionSummaryLoader';

interface IAAEditSubscriptionRenewPageFormProps {
  onContractChanged?: (value: string) => void;
}

const AAEditSubscriptionRenewPageForm = ({
  onContractChanged,
}: IAAEditSubscriptionRenewPageFormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    isSubscriptionUpdating,
    organizationContracts,
    initState,
    isSuggestionDataLoading,
    updateOrganizationSubscription,
    canChangeContract,
    onAddNewContractHandler,
  } = useAAEditSubscriptionRenewForm();

  const { validateErrors } = useFormErrorsActions();

  return (
    <WarningsConfirmationProvider
      submitFunction={updateOrganizationSubscription}
    >
      <WarningsConfirmationModal />
      <Spinner size="20%" isVisible={isSuggestionDataLoading}>
        <Form
          initialValues={{
            ...initState,
            transferAllEligibleMemberships: true,
          }}
          keepDirtyOnReinitialize
          onSubmit={handleSubmit}
          validate={(validate) =>
            validateAAEditRenewSubscriptionForm(
              validate,
              validateErrors(validate),
            )
          }
        >
          {({ values, ...renderProps }) => (
            <FormErrorActivator>
              <form onSubmit={renderProps.handleSubmit}>
                <PageRootColumns>
                  <LeftContainer>
                    <SelectContractForm
                      contracts={organizationContracts}
                      disabled={isSubscriptionUpdating}
                      canChange={canChangeContract}
                      canNotChangeTooltip={t(
                        'phrase.can-not-change-contract-earlier-six-months',
                      )}
                      setDefaultValue={false}
                      onContractChanged={onContractChanged}
                      isClearable
                      onAddNewContract={onAddNewContractHandler}
                    />
                    <AirAmbulanceRenewSubscriptionForm
                      disabled={isSubscriptionUpdating}
                    />
                  </LeftContainer>
                  <RightContainer>
                    <AARenewSubscriptionSummaryLoader
                      pricing={values?.pricing}
                    />
                    <SubscriptionSummaryForm
                      title={t('headers.summary')}
                      subscriptionType={ProductType.AirAmbulance}
                      disabled={isSubscriptionUpdating}
                      isPriceOverridden={values.overwriteAmount}
                    />
                    <FormSubmitButtons
                      handleSubmit={renderProps.handleSubmit}
                      canSaveDraft
                      isProcessing={isSubscriptionUpdating}
                      saveLabel={t('buttons.create')}
                    />
                  </RightContainer>
                </PageRootColumns>
              </form>
            </FormErrorActivator>
          )}
        </Form>
      </Spinner>
    </WarningsConfirmationProvider>
  );
};

export default AAEditSubscriptionRenewPageForm;
